import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Shield, Send, Loader2, Plus, Trash2, MessageSquare, Lock, Sparkles } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BenefitsChat = () => {
  const { token } = useAuth();
  const { hasFullAccess, isPreviewMode } = useSubscription();
  const [sessions, setSessions] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (hasFullAccess) {
      fetchSessions();
    } else {
      setLoadingSessions(false);
    }
  }, [token, hasFullAccess]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchSessions = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/chat/sessions?chat_type=benefits`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSessions(response.data);
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
    } finally {
      setLoadingSessions(false);
    }
  };

  const loadSession = async (sessionId) => {
    try {
      const response = await axios.get(`${API_URL}/api/chat/sessions/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentSession(response.data);
      setMessages(response.data.messages || []);
    } catch (error) {
      toast.error('Failed to load chat');
    }
  };

  const startNewChat = () => {
    setCurrentSession(null);
    setMessages([]);
  };

  const deleteSession = async (sessionId, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API_URL}/api/chat/sessions/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSessions(sessions.filter(s => s.session_id !== sessionId));
      if (currentSession?.session_id === sessionId) {
        startNewChat();
      }
      toast.success('Chat deleted');
    } catch (error) {
      toast.error('Failed to delete chat');
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    if (!hasFullAccess) {
      toast.error('Activate your membership to chat with the AI advisor');
      return;
    }

    const userMessage = { role: 'user', content: input.trim(), timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/chat/benefits`, {
        message: userMessage.content,
        session_id: currentSession?.session_id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const assistantMessage = { 
        role: 'assistant', 
        content: response.data.response,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, assistantMessage]);
      
      if (!currentSession) {
        setCurrentSession({ session_id: response.data.session_id });
        fetchSessions();
      }
    } catch (error) {
      toast.error('Failed to send message');
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="h-[calc(100vh-64px)] lg:h-screen flex" data-testid="benefits-chat-page">
        {/* Sidebar */}
        <div className="hidden md:flex w-72 bg-white dark:bg-navy-800 border-r border-navy-100 dark:border-navy-700 flex-col">
          <div className="p-4 border-b border-navy-100 dark:border-navy-700">
            <Button 
              onClick={startNewChat} 
              className="w-full bg-amber-600 hover:bg-amber-700"
              data-testid="new-chat-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Conversation
            </Button>
          </div>
          
          <ScrollArea className="flex-1 p-4">
            {loadingSessions ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-navy-400" />
              </div>
            ) : sessions.length > 0 ? (
              <div className="space-y-2">
                {sessions.map((session) => (
                  <div
                    key={session.session_id}
                    onClick={() => loadSession(session.session_id)}
                    className={`
                      flex items-center justify-between p-3 rounded-lg cursor-pointer group
                      ${currentSession?.session_id === session.session_id 
                        ? 'bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800' 
                        : 'hover:bg-navy-50 dark:hover:bg-navy-700'}
                    `}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <Shield className="w-4 h-4 text-amber-600 flex-shrink-0" />
                      <span className="text-sm truncate text-navy-700 dark:text-navy-200">
                        {session.messages?.[0]?.content?.slice(0, 30) || 'New chat'}...
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="opacity-0 group-hover:opacity-100 h-8 w-8"
                      onClick={(e) => deleteSession(session.session_id, e)}
                    >
                      <Trash2 className="w-4 h-4 text-navy-400" />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-navy-400 text-sm py-8">No conversations yet</p>
            )}
          </ScrollArea>
        </div>

        {/* Chat Area */}
        <div className="flex-1 flex flex-col bg-navy-50 dark:bg-navy-900">
          {/* Header */}
          <div className="bg-white dark:bg-navy-800 border-b border-navy-100 dark:border-navy-700 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/50 rounded-xl flex items-center justify-center">
                  <Shield className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                </div>
                <div>
                  <h1 className="font-heading font-semibold text-navy-900 dark:text-white">Veterans Benefits Advisor</h1>
                  <p className="text-sm text-navy-500 dark:text-navy-400">Ask about VA healthcare, GI Bill, home loans & more</p>
                </div>
              </div>
              {isPreviewMode && (
                <Link to="/membership">
                  <Button size="sm" className="bg-amber-600 hover:bg-amber-700">
                    <Lock className="w-4 h-4 mr-2" />
                    Unlock
                  </Button>
                </Link>
              )}
            </div>
          </div>

          {/* Preview Mode Banner */}
          {isPreviewMode && (
            <div className="bg-amber-50 dark:bg-amber-900/30 border-b border-amber-200 dark:border-amber-800 px-6 py-3">
              <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400 text-sm">
                <Lock className="w-4 h-4" />
                <span><strong>Preview Mode</strong> - Activate your membership to chat with the AI Benefits Advisor</span>
              </div>
            </div>
          )}

          {/* Messages */}
          <ScrollArea className="flex-1 p-6">
            {messages.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center">
                <div className="w-20 h-20 bg-amber-100 dark:bg-amber-900/50 rounded-2xl flex items-center justify-center mb-6">
                  <Shield className="w-10 h-10 text-amber-600 dark:text-amber-400" />
                </div>
                <h2 className="font-heading text-xl font-semibold text-navy-900 dark:text-white mb-2">
                  {isPreviewMode ? 'AI Benefits Advisor' : 'How can I help you today?'}
                </h2>
                <p className="text-navy-500 dark:text-navy-400 max-w-md mb-8">
                  {isPreviewMode 
                    ? 'Get expert guidance on VA benefits, GI Bill, disability claims, and more. Activate your membership to start chatting.'
                    : "I'm here to guide you through VA benefits, GI Bill questions, disability claims, and more."
                  }
                </p>
                {isPreviewMode ? (
                  <div className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-3 max-w-lg opacity-60">
                      {[
                        'What VA healthcare benefits am I eligible for?',
                        'How do I apply for the GI Bill?',
                        'Explain disability compensation ratings',
                        'What is the VA home loan program?'
                      ].map((suggestion) => (
                        <div
                          key={suggestion}
                          className="text-left py-3 px-4 text-sm border border-navy-200 dark:border-navy-600 rounded-lg"
                        >
                          {suggestion}
                        </div>
                      ))}
                    </div>
                    <Link to="/membership">
                      <Button className="bg-amber-600 hover:bg-amber-700">
                        <Sparkles className="w-4 h-4 mr-2" />
                        Activate Membership to Chat
                      </Button>
                    </Link>
                  </div>
                ) : (
                <div className="grid sm:grid-cols-2 gap-3 max-w-lg">
                  {[
                    'What VA healthcare benefits am I eligible for?',
                    'How do I apply for the GI Bill?',
                    'Explain disability compensation ratings',
                    'What is the VA home loan program?'
                  ].map((suggestion) => (
                    <Button
                      key={suggestion}
                      variant="outline"
                      className="text-left h-auto py-3 px-4 text-sm border-navy-200 dark:border-navy-600 dark:text-white hover:bg-white dark:hover:bg-navy-700 hover:border-amber-300 dark:hover:border-amber-600"
                      onClick={() => setInput(suggestion)}
                    >
                      {suggestion}
                    </Button>
                  ))}
                </div>
                )}
              </div>
            ) : (
              <div className="space-y-4 max-w-3xl mx-auto">
                {messages.map((msg, idx) => (
                  <div
                    key={idx}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`
                        max-w-[80%] rounded-2xl px-4 py-3
                        ${msg.role === 'user' 
                          ? 'bg-navy-900 text-white rounded-br-md' 
                          : 'bg-white dark:bg-navy-800 border border-navy-100 dark:border-navy-700 text-navy-800 dark:text-navy-100 rounded-bl-md shadow-sm'}
                      `}
                    >
                      <p className="whitespace-pre-wrap text-sm leading-relaxed">{msg.content}</p>
                    </div>
                  </div>
                ))}
                {loading && (
                  <div className="flex justify-start">
                    <div className="bg-white dark:bg-navy-800 border border-navy-100 dark:border-navy-700 rounded-2xl rounded-bl-md px-4 py-3 shadow-sm">
                      <Loader2 className="w-5 h-5 animate-spin text-amber-600" />
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            )}
          </ScrollArea>

          {/* Input */}
          <div className="bg-white dark:bg-navy-800 border-t border-navy-100 dark:border-navy-700 p-4">
            <form onSubmit={sendMessage} className="max-w-3xl mx-auto">
              <div className="flex gap-3">
                <Input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask about VA benefits, GI Bill, disability claims..."
                  className="flex-1 h-12 border-navy-200 dark:border-navy-600 dark:bg-navy-700 dark:text-white focus:border-amber-500"
                  disabled={loading}
                  data-testid="chat-input"
                />
                <Button 
                  type="submit" 
                  className="h-12 px-6 bg-amber-600 hover:bg-amber-700"
                  disabled={loading || !input.trim()}
                  data-testid="send-message-btn"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default BenefitsChat;
